import React, { useState, useEffect, useContext } from "react"
import { Link } from "react-router-dom"
import request from "request"
import Flash from "flash"

import { ApplicationContext } from "ApplicationContext"
import Header from "Header"
import Table from "Table"
import AsyncContainer from "AsyncContainer"
import Button from "Button"
import Icon from "Icon"
import WebhookEndpointModal from "./WebhookEndpointModal"
import { EndpointStatusTag, formatTime } from "./webhookHelpers"

const WebhooksList = () => {
  const { CurrentUserContext } = useContext(ApplicationContext)
  const { venue: currentVenue } = CurrentUserContext
  const [isLoaded, setIsLoaded] = useState(false)
  const [endpoints, setEndpoints] = useState([])
  const [availableEvents, setAvailableEvents] = useState([])
  const [editing, setEditing] = useState(null) // null = closed, {} = new, {id...} = edit

  const loadEndpoints = () =>
    request.get("/venue_admin/webhook_endpoints").then(({ data }) => {
      setEndpoints(data.webhookEndpoints || [])
      setIsLoaded(true)
    })

  useEffect(() => {
    let isMounted = true
    Promise.all([
      request.get("/venue_admin/webhook_endpoints"),
      request.get("/venue_admin/webhook_endpoints/events"),
    ]).then(([endpointsRes, eventsRes]) => {
      if (!isMounted) return
      setEndpoints(endpointsRes.data.webhookEndpoints || [])
      setAvailableEvents(eventsRes.data.events || [])
      setIsLoaded(true)
    })
    return () => { isMounted = false }
  }, [currentVenue])

  const handleTest = (endpoint) => {
    request.post(`/venue_admin/webhook_endpoints/${endpoint.id}/test`).then(() => {
      Flash.broadcast("success", `Test event queued for ${endpoint.name}. Check its deliveries in a moment.`)
    })
  }

  const handleToggle = (endpoint) => {
    request
      .patch(`/venue_admin/webhook_endpoints/${endpoint.id}`, { webhook_endpoint: { active: !endpoint.active } })
      .then(loadEndpoints)
  }

  const handleDelete = (endpoint) => {
    if (!confirm(`Delete "${endpoint.name}"? Its delivery history will be deleted too and events will stop being sent to it.`)) return
    request.delete(`/venue_admin/webhook_endpoints/${endpoint.id}`).then(loadEndpoints)
  }

  const HEADERS = {
    name: "Name",
    url: "URL",
    events: "Events",
    active: "Status",
    lastDeliveredAt: "Last Delivered",
    lastFailedAt: "Last Failure",
    actions: "Actions",
  }

  const FORMATTERS = {
    name: (e) => <Link to={`/webhooks/${e.id}`} className="has-text-weight-bold">{e.name}</Link>,
    url: (e) => <code className="is-size-7">{e.url}</code>,
    events: (e) => e.events.map((type) => <span key={type} className="tag is-light mr-1">{type}</span>),
    active: (e) => (
      <span>
        <EndpointStatusTag endpoint={e} />
        {e.disabledReason && <p className="help has-text-danger">{e.disabledReason}</p>}
      </span>
    ),
    lastDeliveredAt: (e) => formatTime(e.lastDeliveredAt),
    lastFailedAt: (e) => formatTime(e.lastFailedAt),
    actions: (e) => (
      <div className="buttons are-small">
        <Button text="Send test" size="small" onClick={() => handleTest(e)} />
        <Button text={e.active ? "Deactivate" : "Activate"} size="small" onClick={() => handleToggle(e)} />
        <Button text={<Icon icon="edit" />} size="small" onClick={() => setEditing(e)} />
        <Button text={<Icon icon="trash" />} color="danger" size="small" onClick={() => handleDelete(e)} />
      </div>
    ),
  }

  return (
    <div>
      <Header text="Webhooks" />

      <div className="block">
        <div className="content">
          <p>
            Webhooks notify an external system in real time when something happens in this venue, for example when a
            prepaid credit is redeemed at the POS. Catch sends a signed JSON <code>POST</code> to each endpoint subscribed
            to the event and retries for about two days if the endpoint does not answer with a 2xx.
          </p>
          <p>
            Click an endpoint's name to see every delivery with its payload, the response it got, and to resend it.
            The payload format and signature verification are documented at{" "}
            <a href="/api-docs" target="_blank" rel="noreferrer">/api-docs</a>.
          </p>
        </div>
      </div>

      <div className="block">
        <Button text="Add Webhook Endpoint" color="primary" icon="plus" onClick={() => setEditing({})} />
      </div>

      <div className="block">
        <AsyncContainer isLoaded={isLoaded}>
          {endpoints.length === 0 ? (
            <div className="notification is-info">
              <p>No webhook endpoints yet. Click "Add Webhook Endpoint" to register a URL.</p>
            </div>
          ) : (
            <Table rows={endpoints} headers={HEADERS} formatters={FORMATTERS} />
          )}
        </AsyncContainer>
      </div>

      <WebhookEndpointModal
        isOpen={editing !== null}
        endpoint={editing?.id ? editing : null}
        availableEvents={availableEvents}
        onClose={() => setEditing(null)}
        onSaved={loadEndpoints}
      />
    </div>
  )
}

export default WebhooksList
